// Generates a 1080×1080 PNG match-highlight social card.
// Pure canvas — no external deps. Triggers a download in the browser.

const teamHue = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return h % 360;
};

const initials = (name = '') => {
  const stripped = name.replace(/[^A-Za-z\s]/g, '').split(/\s+/).filter(Boolean);
  if (stripped.length === 1) return stripped[0].slice(0, 2).toUpperCase();
  return (stripped[0][0] + stripped[stripped.length - 1][0]).toUpperCase();
};

const drawCrest = (ctx, name, x, y, size, scoreSide = 'home') => {
  const hue = teamHue(name + (scoreSide === 'away' ? 'X' : ''));
  // Shield path
  ctx.save();
  ctx.translate(x, y);
  const grad = ctx.createLinearGradient(0, -size / 2, 0, size / 2);
  grad.addColorStop(0, `hsl(${hue}, 78%, 52%)`);
  grad.addColorStop(1, `hsl(${hue}, 75%, 22%)`);
  ctx.fillStyle = grad;
  ctx.strokeStyle = `hsl(${hue}, 90%, 75%)`;
  ctx.lineWidth = size * 0.04;

  ctx.beginPath();
  const s = size / 2;
  ctx.moveTo(0, -s);
  ctx.lineTo(s * 0.85, -s * 0.7);
  ctx.lineTo(s * 0.85, s * 0.1);
  ctx.quadraticCurveTo(s * 0.85, s * 0.7, 0, s);
  ctx.quadraticCurveTo(-s * 0.85, s * 0.7, -s * 0.85, s * 0.1);
  ctx.lineTo(-s * 0.85, -s * 0.7);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Initials
  ctx.fillStyle = '#FFFFFF';
  ctx.font = `900 ${size * 0.42}px 'Bebas Neue', 'Barlow Condensed', Impact, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(initials(name), 0, size * 0.04);
  ctx.restore();
};

/**
 * Generate and trigger a download for a match highlight social card.
 *
 * @param {Object} opts
 * @param {Object} opts.homeTeam - team object with `name`, `players`, `lineup`
 * @param {Object} opts.awayTeam - team object with `name`, `players`, `lineup`
 * @param {number} opts.homeScore
 * @param {number} opts.awayScore
 * @param {Object} opts.matchRatings - { [playerId]: number }
 * @param {Array}  opts.events - events from simulation (used for Top Performer)
 * @param {string} [opts.competition] - label like "JWFL FRIENDLY" / "PREMIER LEAGUE"
 * @returns {Promise<string>} a data URL of the generated PNG
 */
export const generateMatchHighlightCard = async ({
  homeTeam, awayTeam, homeScore = 0, awayScore = 0,
  matchRatings = {}, events = [], competition = 'JWFL FRIENDLY',
}) => {
  const W = 1080, H = 1080;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  // ===== Background — radial dark gradient =====
  const bg = ctx.createRadialGradient(W / 2, H / 2, 100, W / 2, H / 2, 800);
  bg.addColorStop(0, '#0F1A2E');
  bg.addColorStop(0.6, '#04070D');
  bg.addColorStop(1, '#000000');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Diagonal accent stripes (broadcast feel)
  ctx.save();
  ctx.translate(W / 2, H / 2);
  ctx.rotate(-Math.PI / 12);
  ctx.translate(-W / 2, -H / 2);
  for (let i = 0; i < 16; i++) {
    ctx.fillStyle = i % 2 === 0 ? 'rgba(255, 255, 255, 0.012)' : 'rgba(212, 169, 51, 0.018)';
    ctx.fillRect(-200 + i * 90, -200, 50, H + 400);
  }
  ctx.restore();

  // Big team color glow halos
  const homeHue = teamHue(homeTeam.name);
  const awayHue = teamHue(awayTeam.name + 'X');
  ctx.fillStyle = `hsla(${homeHue}, 80%, 50%, 0.25)`;
  ctx.beginPath(); ctx.arc(180, 540, 360, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = `hsla(${awayHue}, 80%, 50%, 0.25)`;
  ctx.beginPath(); ctx.arc(900, 540, 360, 0, Math.PI * 2); ctx.fill();

  // Center vertical divider with glow
  const div = ctx.createLinearGradient(0, 0, 0, H);
  div.addColorStop(0, 'rgba(212,169,51,0)');
  div.addColorStop(0.5, 'rgba(212,169,51,0.6)');
  div.addColorStop(1, 'rgba(212,169,51,0)');
  ctx.fillStyle = div;
  ctx.fillRect(W / 2 - 1, 280, 2, 520);

  // ===== Header strip =====
  ctx.fillStyle = '#D4A933';
  ctx.fillRect(0, 0, W, 12);
  ctx.fillStyle = '#0A0A0C';
  ctx.fillRect(0, 12, W, 64);
  ctx.fillStyle = '#D4A933';
  ctx.font = "900 20px 'JetBrains Mono', monospace";
  ctx.textAlign = 'left';
  ctx.fillText('JWFL', 50, 50);
  ctx.fillStyle = '#FFFFFF';
  ctx.font = "700 16px 'JetBrains Mono', monospace";
  ctx.fillText('· ' + competition.toUpperCase(), 130, 50);
  ctx.textAlign = 'right';
  ctx.fillStyle = '#7A8FAB';
  ctx.font = "600 14px 'JetBrains Mono', monospace";
  const date = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  ctx.fillText(`MATCH HIGHLIGHT · ${date.toUpperCase()}`, W - 50, 50);

  // ===== Crests =====
  drawCrest(ctx, homeTeam.name, 270, 380, 260, 'home');
  drawCrest(ctx, awayTeam.name, W - 270, 380, 260, 'away');

  // ===== Team names =====
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'center';
  ctx.font = "900 42px 'Bebas Neue', 'Barlow Condensed', Impact, sans-serif";
  ctx.fillText(homeTeam.name.toUpperCase(), 270, 560);
  ctx.fillText(awayTeam.name.toUpperCase(), W - 270, 560);

  // ===== Score block (center) =====
  ctx.fillStyle = '#FFFFFF';
  ctx.font = "900 200px 'Bebas Neue', 'Barlow Condensed', Impact, sans-serif";
  ctx.textAlign = 'center';
  ctx.fillText(`${homeScore} : ${awayScore}`, W / 2, 470);

  // FT badge under score
  ctx.fillStyle = '#D4A933';
  ctx.font = "900 28px 'JetBrains Mono', monospace";
  ctx.fillText('FULL TIME', W / 2, 540);

  // ===== Top performers panel =====
  const cardY = 660;
  const cardH = 320;
  ctx.fillStyle = 'rgba(10, 22, 39, 0.85)';
  ctx.strokeStyle = 'rgba(212, 169, 51, 0.4)';
  ctx.lineWidth = 2;
  ctx.fillRect(60, cardY, W - 120, cardH);
  ctx.strokeRect(60, cardY, W - 120, cardH);

  ctx.fillStyle = '#D4A933';
  ctx.fillRect(60, cardY, 6, cardH);

  ctx.textAlign = 'left';
  ctx.fillStyle = '#D4A933';
  ctx.font = "900 22px 'JetBrains Mono', monospace";
  ctx.fillText('TOP PERFORMERS', 100, cardY + 50);

  // Helper — pull XI from lineup with rating
  const xiWith = (team) => (team.lineup || []).map(num => team.players.find(p => p.number === num))
    .filter(Boolean)
    .map(p => ({ ...p, rating: matchRatings[p.id] || 6.5 }));
  const homeXI = xiWith(homeTeam).sort((a, b) => b.rating - a.rating).slice(0, 3);
  const awayXI = xiWith(awayTeam).sort((a, b) => b.rating - a.rating).slice(0, 3);

  const ratingBg = (r) => {
    if (r >= 8.5) return '#FFD700';
    if (r >= 7.5) return '#32D74B';
    if (r >= 6.5) return '#4DA3FF';
    if (r >= 5.5) return '#A0A0A5';
    return '#FF3B30';
  };

  const drawSide = (xi, x, label, hue) => {
    ctx.fillStyle = `hsl(${hue}, 80%, 60%)`;
    ctx.font = "700 14px 'JetBrains Mono', monospace";
    ctx.textAlign = 'left';
    ctx.fillText(label.toUpperCase(), x, cardY + 90);
    xi.forEach((p, i) => {
      const y = cardY + 130 + i * 60;
      // rating chip
      const r = (p.rating || 6.5).toFixed(1);
      ctx.fillStyle = ratingBg(p.rating);
      ctx.fillRect(x, y - 22, 60, 36);
      ctx.fillStyle = '#000';
      ctx.font = "900 22px 'JetBrains Mono', monospace";
      ctx.textAlign = 'center';
      ctx.fillText(r, x + 30, y + 2);
      // name + position
      ctx.textAlign = 'left';
      ctx.fillStyle = '#FFFFFF';
      ctx.font = "700 22px 'Barlow Condensed', Impact, sans-serif";
      const fullName = (p.name || 'Player').toUpperCase();
      const truncated = fullName.length > 22 ? fullName.slice(0, 22) + '…' : fullName;
      ctx.fillText(truncated, x + 80, y - 2);
      ctx.fillStyle = '#7A8FAB';
      ctx.font = "600 13px 'JetBrains Mono', monospace";
      ctx.fillText(`${p.position} · #${p.number}`, x + 80, y + 18);
    });
  };
  drawSide(homeXI, 100, homeTeam.name, homeHue);
  drawSide(awayXI, W / 2 + 30, awayTeam.name, awayHue);

  // ===== Footer =====
  ctx.fillStyle = '#0A0A0C';
  ctx.fillRect(0, H - 56, W, 56);
  ctx.fillStyle = '#D4A933';
  ctx.fillRect(0, H - 8, W, 8);

  ctx.fillStyle = '#FFFFFF';
  ctx.font = "900 22px 'Bebas Neue', 'Barlow Condensed', Impact, sans-serif";
  ctx.textAlign = 'center';
  const goalCount = events.filter(e => e.type === 'goal').length;
  ctx.fillText(
    `${goalCount} GOALS · BUILT IN JWFL · #JAPANWORLDFOOTBALLLEAGUE`,
    W / 2, H - 26
  );

  return canvas.toDataURL('image/png');
};

export const downloadHighlightCard = async (opts) => {
  const url = await generateMatchHighlightCard(opts);
  const a = document.createElement('a');
  a.href = url;
  const safe = (s) => s.replace(/[^A-Za-z0-9]/g, '_');
  a.download = `JWFL_${safe(opts.homeTeam.name)}_${opts.homeScore}-${opts.awayScore}_${safe(opts.awayTeam.name)}.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  return url;
};
